/**
 * useCustomDataTypes — ViewModel for /manage/custom-data-types.
 *
 * Per SOP 02 §"MVVM Pattern" + §"List Pagination". Composes
 * usePaginatedList for the read path and owns six mutations
 * (type create/rename/delete + value add/rename/delete).
 *
 * Mirrors src/viewmodels/useAttributes.ts in shape.
 */

import { useCallback } from 'react'
import { useAuth } from '../context/AuthContext'
import { APIROUTES, fillPath } from '../constants/routes.constants'
import type {
  CustomDataTypeDto,
  CustomDataTypeValueDto
} from '../models/custom-data-type.model'
import { mutateJson } from '../utils/api/mutateJson'
import { parseApiError } from '../utils/api/parseApiError'
import { usePaginatedList } from './usePaginatedList'

// Single-source-of-truth: src/models/custom-data-type.model.ts.
// Per SOP 02 §"Mutation Patterns (LOCKED)" — DTO source rule.
export type { CustomDataTypeDto, CustomDataTypeValueDto } from '../models/custom-data-type.model'

export interface UseCustomDataTypesReturn {
  readonly items: CustomDataTypeDto[]
  readonly total: number
  readonly page: number
  readonly pageSize: number
  readonly isLoading: boolean
  readonly error: string | null
  readonly setPage: (n: number) => void
  readonly reload: () => void
  readonly createType: (
    type_name: string,
    is_axes: boolean
  ) => Promise<{ ok: true } | { ok: false; field: string | null; message: string }>
  readonly updateType: (
    id: string,
    patch: { type_name?: string; is_axes?: boolean }
  ) => Promise<{ ok: true } | { ok: false; field: string | null; message: string }>
  readonly deleteType: (
    id: string
  ) => Promise<{ ok: true } | { ok: false; message: string }>
  readonly addValue: (
    typeId: string,
    value: string
  ) => Promise<{ ok: true } | { ok: false; field: string | null; message: string }>
  readonly renameValue: (
    valueId: string,
    value: string
  ) => Promise<{ ok: true } | { ok: false; field: string | null; message: string }>
  readonly deleteValue: (
    valueId: string
  ) => Promise<{ ok: true } | { ok: false; message: string }>
}

interface ListBody {
  readonly success: true
  readonly data: {
    readonly items: CustomDataTypeDto[]
    readonly total: number
    readonly page: number
    readonly pageSize: number
  }
}

export function useCustomDataTypes(): UseCustomDataTypesReturn {
  const { authedFetch } = useAuth()

  const paged = usePaginatedList<CustomDataTypeDto, undefined>({
    fetcher: useCallback(
      async (page, pageSize, signal) => {
        const url = `${APIROUTES.MANAGECUSTOMDATATYPES}?page=${page}&pageSize=${pageSize}`
        const res = await authedFetch(url, { signal })
        if (!res.ok) {
          const { message } = await parseApiError(res)
          throw new Error(message)
        }
        const body = (await res.json()) as ListBody
        return {
          items: body.data.items,
          total: body.data.total
        }
      },
      [authedFetch]
    )
  })

  /**
   * Helper for value-side mutations — they don't return a parent type
   * DTO; we splice the change into the parent's `values` array in
   * place and feed the new parent through `updateItem` so the list
   * row re-renders without a full GET.
   */
  const updateParentValues = useCallback(
    (typeId: string, mutator: (values: CustomDataTypeValueDto[]) => CustomDataTypeValueDto[]) => {
      const current = paged.items.find((t) => t.id === typeId)
      if (current === undefined) return
      paged.updateItem(
        (it) => it.id === typeId,
        { ...current, values: mutator(current.values) }
      )
    },
    [paged]
  )

  const createType = useCallback(
    async (type_name: string, is_axes: boolean) => {
      const r = await mutateJson<CustomDataTypeDto>(
        authedFetch,
        APIROUTES.MANAGECUSTOMDATATYPES,
        'POST',
        { type_name, is_axes }
      )
      if (!r.ok) return r
      if (paged.page === 1) paged.insertItem(r.data)
      else paged.reload()
      return { ok: true as const }
    },
    [authedFetch, paged]
  )

  const updateType = useCallback(
    async (id: string, patch: { type_name?: string; is_axes?: boolean }) => {
      const r = await mutateJson<CustomDataTypeDto>(
        authedFetch,
        fillPath(APIROUTES.MANAGECUSTOMDATATYPEITEM, { id }),
        'PATCH',
        patch
      )
      if (!r.ok) return r
      paged.updateItem((it) => it.id === id, r.data)
      return { ok: true as const }
    },
    [authedFetch, paged]
  )

  const deleteType = useCallback(
    async (id: string) => {
      const r = await mutateJson<{ ok: true }>(
        authedFetch,
        fillPath(APIROUTES.MANAGECUSTOMDATATYPEITEM, { id }),
        'DELETE'
      )
      if (!r.ok) return { ok: false as const, message: r.message }
      paged.removeItem((it) => it.id === id)
      return { ok: true as const }
    },
    [authedFetch, paged]
  )

  const addValue = useCallback(
    async (typeId: string, value: string) => {
      const r = await mutateJson<CustomDataTypeValueDto>(
        authedFetch,
        fillPath(APIROUTES.MANAGECUSTOMDATATYPEVALUES, { id: typeId }),
        'POST',
        { value }
      )
      if (!r.ok) return r
      updateParentValues(typeId, (vs) => [...vs, r.data])
      return { ok: true as const }
    },
    [authedFetch, updateParentValues]
  )

  const renameValue = useCallback(
    async (valueId: string, value: string) => {
      const r = await mutateJson<CustomDataTypeValueDto>(
        authedFetch,
        fillPath(APIROUTES.MANAGECUSTOMDATATYPEVALUEITEM, { id: valueId }),
        'PATCH',
        { value }
      )
      if (!r.ok) return r
      updateParentValues(r.data.custom_data_type_id, (vs) =>
        vs.map((v) => (v.id === valueId ? r.data : v))
      )
      return { ok: true as const }
    },
    [authedFetch, updateParentValues]
  )

  const deleteValue = useCallback(
    async (valueId: string) => {
      const r = await mutateJson<{ ok: true }>(
        authedFetch,
        fillPath(APIROUTES.MANAGECUSTOMDATATYPEVALUEITEM, { id: valueId }),
        'DELETE'
      )
      if (!r.ok) return { ok: false as const, message: r.message }
      // No parent id in a DELETE response (server returns {ok:true} per
      // SOP). Walk all types and drop the matching value from whichever
      // contains it. Cheap — the list is small + already in memory.
      for (const t of paged.items) {
        if (t.values.some((v) => v.id === valueId)) {
          updateParentValues(t.id, (vs) => vs.filter((v) => v.id !== valueId))
          break
        }
      }
      return { ok: true as const }
    },
    [authedFetch, paged, updateParentValues]
  )

  return {
    items: paged.items,
    total: paged.total,
    page: paged.page,
    pageSize: paged.pageSize,
    isLoading: paged.isLoading,
    error: paged.error,
    setPage: paged.setPage,
    reload: paged.reload,
    createType,
    updateType,
    deleteType,
    addValue,
    renameValue,
    deleteValue
  }
}
